<!--
  Created 10.10.2023
  Intro component for the proposal show page.
-->
<template>
  <section class="container relative isolate mx-auto px-6 py-12 pb-16 print:break-before-page print:!py-8 md:py-24 md:pb-28">
    <!-- Settings button -->
    <Tooltip
      v-if="contentEditable"
      class="!absolute right-4 top-4 z-20"
      align="right"
      content-class="ml-4 mt-2 bg-gray-900 text-gray-100 w-56 p-2 rounded opacity-95 translate-x-[calc(-100%+0.5rem)] w-max">
      <template #trigger>
        <AdjustmentsHorizontalIcon
          class="h-7 w-7 rounded-md text-gray-400 drop-shadow-sm hover:text-gray-500"
          aria-hidden="true"
          @click="openIntroSettingsModal" />
      </template>
      <template #content>
        <p class="text-xs">
          Click to edit intro settings.
        </p>
      </template>
    </Tooltip>
    <div class="flex flex-col max-lg:space-y-10 lg:flex-row">
      <!-- Text box -->
      <div class="flex flex-col items-center justify-center lg:basis-1/2 lg:pr-12">
        <TextEditableTextarea
          v-if="contentEditable && proposalDestination"
          id="proposal-heading"
          v-model="proposalDestination.heading"
          class="w-full"
          background-style="gray"
          :text-area-class="'bg-transparent mb-8 w-full text-2xl font-medium text-gray-800 md:text-3xl lg:text-2xl xl:text-3xl ' +
            'placeholder:italic placeholder:text-gray-600 placeholder:opacity-50 cursor-text'"
          :placeholder="'Destination heading here..'"
          :show-buttons="false"
          :saved-changes="savedChanges"
          @edited="store.unsavedChanges.changes.proposal = true;" />
        <h1
          v-else
          class="mb-8 w-full whitespace-pre-line text-2xl font-medium text-gray-800 md:text-3xl lg:text-2xl xl:text-3xl">
          {{ proposalDestination.heading }}
        </h1>
        <TextEditableTextarea
          v-if="contentEditable && proposalDestination"
          id="proposal-description"
          v-model="proposalDestination.description"
          :tab-rows="4"
          class="w-full"
          background-style="gray"
          :text-area-class="'bg-transparent text-md text-gray-600 lg:text-lg ' +
            'placeholder:italic placeholder:text-gray-600 placeholder:opacity-50 cursor-text'"
          :placeholder="'Destination description here..'"
          :show-buttons="false"
          :saved-changes="savedChanges"
          @edited="store.unsavedChanges.changes.proposal = true;" />
        <p
          v-else-if="proposalDestination.description"
          class="text-md whitespace-pre-line text-gray-600 lg:text-lg">
          <StringLimiter
            v-if="!store.printMode"
            :string="proposalDestination.description" />
          <span v-else>
            {{ proposalDestination.description }}
          </span>
        </p>
      </div>
      <!-- Image -->
      <div class="group relative flex flex-col justify-center lg:basis-1/2 lg:pl-12">
        <MediaFigure
          :image="image"
          :image-alt="image?.custom_properties?.alt_description ?? ''"
          :fig-caption="image?.name ?? ''"
          image-classes="rounded-md"
          fig-caption-outside />
        <div
          v-if="contentEditable && proposal"
          class="absolute inset-0 ml-12 p-16">
          <div class="mx-auto max-w-7xl">
            <media-select-single
              :selected="image"
              :settings="{
                proposal: {
                  name: 'Current Proposal',
                  get: route('api.proposal.media.index', {proposal: proposal.id}),
                  upload: route('api.proposal.media.store', {proposal: proposal.id})
                },
                destination: {
                  name: proposal.destinations[0]?.name ?? 'Original destination',
                  get: route('api.destination.entity.media.index', {entity: proposal.destinations[0]?.id}),
                  upload: route('api.destination.entity.media.store', {entity: proposal.destinations[0]?.id})
                }
              }"
              :default_search="proposal.destinations[0] ? proposal.destinations[0]?.name + ', ' + proposal.destinations[0]?.parent?.name : ''"
              :cropable="typeof image !== 'string'"
              :coordinates="imageCrop ? imageCrop.coordinates : null"
              hide-media-update-buttons
              @update="updateMedia"
              @crop="cropped" />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
<script setup>
import Media from '@/Pages/Proposal/Components/Media.vue';
import StringLimiter from '@/Components/StringLimiter.vue';
import { useProposalStore } from '@/Stores/Proposal';
import {
  computed, onBeforeMount, onMounted, reactive, ref, watch,
} from 'vue';
import TextEditableTextarea from '@/Components/Forms/TextEditableTextarea.vue';
import Cookies from 'js-cookie';
import {
  uniqueId, isEmptyObject,
} from '@/utils';
import MediaFigure from '@/Pages/Proposal/Components/MediaFigure.vue';
import MediaSelectSingle from '@/Components/Media/MediaPickerSingle.vue';
import Tooltip from '@/Components/Tooltip.vue';
import { AdjustmentsHorizontalIcon } from '@heroicons/vue/24/outline';
import { notify } from 'notiwind';

/* *********************************************************************
 * Component declarations
 * ****************************************************************** */

const props = defineProps({
  contentEditable: {
    type: Boolean,
    default: false,
  },
});

const currentLanguage = ref(Cookies.get('currentLanguage') ?? 'no');

const store = useProposalStore();
const savedChanges = computed(() => !store.unsavedChanges.changes.proposal);
const proposal = computed(() => store.proposal ?? {});
const proposalDestination = computed(() => proposal.value?.data?.destination);

const image = computed(() => proposalDestination.value?.image ?? null);
const imageCrop = computed(() => proposalDestination.value?.media_crop ?? null);

const showIntroSettingsModal = ref(false);

/* *********************************************************************
 * Component functions
 * ****************************************************************** */

/**
 * Update media function for media picker
 * @param {object} media - Media object
 */
function updateMedia(media) {
  proposalDestination.value.image = media;
  proposalDestination.value.media_id = media.id;
  store.unsavedChanges.changes.proposal = true;
}

/**
 * Update media crop function for media picker
 * @param {object} data - Media crop data
 */
function cropped(data) {
  proposalDestination.value.media_crop = data;
  store.unsavedChanges.changes.proposal = true;
}

/**
 * WIP: Open intro settings modal
 */
function openIntroSettingsModal() {
  showIntroSettingsModal.value = true;
  notify({
    type: 'info',
    title: 'Feature not implemented',
    text: 'Editing of intro settings is not yet implemented.',
  }, 4000);
}

/* *********************************************************************
 * Page lifecycle functions
 * ****************************************************************** */

onMounted(() => {
  // if store destination data is empty, we fetch data from destination if available
  if ((isEmptyObject(proposalDestination.value) || !proposalDestination.value?.heading) && proposal.value.destinations[0]) {
    if (proposal.value.destinations[0]?.content_versions?.length > 0) {
      proposalDestination.value.name = proposal.value.destinations[0].name ?? '';
      proposalDestination.value.heading = proposal.value.destinations[0].content_versions[0]?.texts[currentLanguage.value]?.heading ?? proposal.value.destinations[0].name ?? '';
      proposalDestination.value.description = proposal.value.destinations[0].content_versions[0]?.texts[currentLanguage.value]?.description ?? '';
      proposalDestination.value.proposalProgramText = proposal.value.destinations[0].content_versions[0]?.texts[currentLanguage.value]?.proposalProgramText ?? '';
      proposalDestination.value.timezone = proposal.value.destinations[0].timezone ?? null;
      proposalDestination.value.image = proposal.value.destinations[0].content_versions[0]?.data?.image ?? null;
      proposalDestination.value.destination = proposal.value.destinations[0];
      setTimeout(() => {
        store.unsavedChanges.changes.proposal = true;
      }, 1000);
    }
  }
});

</script>
